import {
  NodeKind,
  TransformKind,
  type CompositionNode,
  type CollageNode,
  type Document,
  type NodeId,
  type SourceId,
} from '@/core/document/types'
import { getCollageTemplate } from '@/content/collageTemplates'
import { getOutputPreset } from '@/content/instagram'
import { getFilmFormat } from '@/content/instantFilm'
import { collageCellRect } from '@/core/document/collage'
import { containedRootRect, outputRatio } from '@/core/document/output'
import { orientedSize, type AspectRatio, type Rect, type Size } from '@/core/geometry'
import { FrameStyleId, PhotoPlacement, PLAIN_FRAME_INNER_SHORT_EDGE_MM } from '@/tools/frame/params'

function unknownNodeKind(node: CompositionNode): Error {
  return new Error(`Document contains unknown node kind "${String((node as unknown as { kind: unknown }).kind)}"`)
}

export function mapNodes(
  root: CompositionNode,
  mapper: (node: CompositionNode) => CompositionNode,
): CompositionNode {
  switch (root.kind) {
    case NodeKind.Image:
      return mapper(root)
    case NodeKind.Frame:
      return mapper({ ...root, child: mapNodes(root.child, mapper) })
    case NodeKind.Collage:
      return mapper({
        ...root,
        cells: root.cells.map((child) => child ? mapNodes(child, mapper) : null),
        tray: root.tray.map((child) => mapNodes(child, mapper)),
      })
    default:
      throw unknownNodeKind(root)
  }
}

export function findNode(root: CompositionNode, id: NodeId): CompositionNode | null {
  if (root.id === id) return root
  switch (root.kind) {
    case NodeKind.Image:
      return null
    case NodeKind.Frame:
      return findNode(root.child, id)
    case NodeKind.Collage:
      for (const child of [...root.cells, ...root.tray]) {
        if (!child) continue
        const found = findNode(child, id)
        if (found) return found
      }
      return null
    default:
      throw unknownNodeKind(root)
  }
}

export function pathToNode(root: CompositionNode, id: NodeId): CompositionNode[] {
  if (root.id === id) return [root]
  switch (root.kind) {
    case NodeKind.Image:
      return []
    case NodeKind.Frame: {
      const childPath = pathToNode(root.child, id)
      return childPath.length > 0 ? [root, ...childPath] : []
    }
    case NodeKind.Collage:
      for (const child of [...root.cells, ...root.tray]) {
        if (!child) continue
        const childPath = pathToNode(child, id)
        if (childPath.length > 0) return [root, ...childPath]
      }
      return []
    default:
      throw unknownNodeKind(root)
  }
}

export interface CollageCellContext {
  collage: CollageNode
  cellIndex: number
  cellRoot: CompositionNode
}

export function collageCellContext(root: CompositionNode, id: NodeId): CollageCellContext | null {
  const path = pathToNode(root, id)
  for (let index = path.length - 2; index >= 0; index -= 1) {
    const candidate = path[index]
    const cellRoot = path[index + 1]
    if (candidate?.kind !== NodeKind.Collage || !cellRoot) continue
    const cellIndex = candidate.cells.findIndex((child) => child?.id === cellRoot.id)
    if (cellIndex >= 0) return { collage: candidate, cellIndex, cellRoot }
  }
  return null
}

export function replaceNode(
  root: CompositionNode,
  id: NodeId,
  next: CompositionNode,
): CompositionNode {
  if (!findNode(root, id)) throw new Error(`Document node "${id}" was not found`)

  function replaceKnownNode(current: CompositionNode): CompositionNode {
    if (current.id === id) return next
    switch (current.kind) {
      case NodeKind.Image:
        return current
      case NodeKind.Frame:
        return { ...current, child: replaceKnownNode(current.child) }
      case NodeKind.Collage:
        return {
          ...current,
          cells: current.cells.map((child) => child ? replaceKnownNode(child) : null),
          tray: current.tray.map(replaceKnownNode),
        }
      default:
        throw unknownNodeKind(current)
    }
  }

  return replaceKnownNode(root)
}

export function firstImageNode(root: CompositionNode): Extract<CompositionNode, { kind: NodeKind.Image }> {
  const image = firstImageNodeOrNull(root)
  if (!image) throw new Error(`Composition node "${root.id}" does not contain an image`)
  return image
}

export function imageNodesInOrder(root: CompositionNode): Extract<CompositionNode, { kind: NodeKind.Image }>[] {
  switch (root.kind) {
    case NodeKind.Image:
      return [root]
    case NodeKind.Frame:
      return imageNodesInOrder(root.child)
    case NodeKind.Collage:
      return [...root.cells, ...root.tray].flatMap((child) => child ? imageNodesInOrder(child) : [])
    default:
      throw unknownNodeKind(root)
  }
}

export function firstImageNodeOrNull(root: CompositionNode): Extract<CompositionNode, { kind: NodeKind.Image }> | null {
  return imageNodesInOrder(root)[0] ?? null
}

export function firstRenderedImageNodeOrNull(root: CompositionNode): Extract<CompositionNode, { kind: NodeKind.Image }> | null {
  switch (root.kind) {
    case NodeKind.Image:
      return root
    case NodeKind.Frame:
      return firstRenderedImageNodeOrNull(root.child)
    case NodeKind.Collage:
      for (const child of root.cells) {
        if (!child) continue
        const image = firstRenderedImageNodeOrNull(child)
        if (image) return image
      }
      return null
    default:
      throw unknownNodeKind(root)
  }
}

export function findRenderedNode(root: CompositionNode, id: NodeId): CompositionNode | null {
  if (root.id === id) return root
  switch (root.kind) {
    case NodeKind.Image:
      return null
    case NodeKind.Frame:
      return findRenderedNode(root.child, id)
    case NodeKind.Collage:
      for (const child of root.cells) {
        if (!child) continue
        const found = findRenderedNode(child, id)
        if (found) return found
      }
      return null
    default:
      throw unknownNodeKind(root)
  }
}

export function parentNode(root: CompositionNode, id: NodeId): CompositionNode | null {
  const path = pathToNode(root, id)
  if (path.length === 0) throw new Error(`Cannot resolve the parent of unknown node "${id}"`)
  return path.at(-2) ?? null
}

export function containsNodeKind(root: CompositionNode, kind: NodeKind): boolean {
  let contains = false
  mapNodes(root, (node) => {
    if (node.kind === kind) contains = true
    return node
  })
  return contains
}

export function containsFrameStyle(root: CompositionNode, styleId: FrameStyleId): boolean {
  let contains = false
  mapNodes(root, (node) => {
    if (node.kind === NodeKind.Frame && node.params.styleId === styleId) contains = true
    return node
  })
  return contains
}

export function nodeRenderRect(
  document: Document,
  sources: ReadonlyMap<SourceId, Size>,
  node: CompositionNode,
  containerSize: Size,
): Rect {
  if (node.kind !== NodeKind.Frame || node.params.styleId !== FrameStyleId.Polaroid) {
    return { x: 0, y: 0, ...containerSize }
  }
  return containedRootRect(nativeSize(document, sources, node.id), containerSize)
}

export function intrinsicRatio(
  root: CompositionNode,
  sources: ReadonlyMap<SourceId, Size>,
): AspectRatio {
  switch (root.kind) {
    case NodeKind.Image: {
      const source = sources.get(root.sourceId)
      if (!source) throw new Error(`Image node "${root.id}" references missing source "${root.sourceId}"`)
      const size = orientedSize(source, root.transforms[TransformKind.Orientation])
      return { w: size.width, h: size.height }
    }
    case NodeKind.Frame:
      if (root.params.styleId === FrameStyleId.Polaroid) {
        const film = getFilmFormat(root.params.formatId).filmMm
        return { w: film.width, h: film.height }
      } else {
        const childRatio = intrinsicRatio(root.child, sources)
        const ratio = childRatio.w / childRatio.h
        const inner = ratio >= 1
          ? { width: PLAIN_FRAME_INNER_SHORT_EDGE_MM * ratio, height: PLAIN_FRAME_INNER_SHORT_EDGE_MM }
          : { width: PLAIN_FRAME_INNER_SHORT_EDGE_MM, height: PLAIN_FRAME_INNER_SHORT_EDGE_MM / ratio }
        return {
          w: inner.width + root.params.borderMm * 2,
          h: inner.height + root.params.borderMm * 2,
        }
      }
    case NodeKind.Collage: {
      const template = getCollageTemplate(root.templateId)
      const presetId = template.goodFor[0]
      if (!presetId) throw new Error(`Collage template "${template.id}" has no configured output preset`)
      const ratio = getOutputPreset(presetId).ratio
      if (!ratio) throw new Error(`Collage template "${template.id}" must target a fixed output ratio first`)
      return ratio
    }
    default:
      throw unknownNodeKind(root)
  }
}

export function documentOutputRatio(
  document: Document,
  sources: ReadonlyMap<SourceId, Size>,
): AspectRatio {
  return outputRatio(document.output, intrinsicRatio(document.root, sources))
}

export function effectiveRatio(
  document: Document,
  sources: ReadonlyMap<SourceId, Size>,
  nodeId: NodeId,
): AspectRatio {
  const path = pathToNode(document.root, nodeId)
  const node = path.at(-1)
  if (!node) {
    throw new Error(`Cannot resolve an effective ratio for unknown node "${nodeId}"`)
  }
  if (node.kind !== NodeKind.Image) throw new Error(`Effective crop ratio requires an Image node, received "${node.kind}"`)
  const parent = path.at(-2)
  if (parent?.kind === NodeKind.Frame) {
    if (parent.params.styleId === FrameStyleId.Polaroid && parent.params.placement === PhotoPlacement.Fill) {
      const window = getFilmFormat(parent.params.formatId).windowMm
      return { w: window.width, h: window.height }
    }
    const crop = node.transforms[TransformKind.Crop]
    if (!crop) throw new Error('A framed image without a fixed window requires a crop rectangle')
    return { w: crop.rect.width, h: crop.rect.height }
  }
  if (parent?.kind === NodeKind.Collage) {
    const cellIndex = parent.cells.findIndex((child) => child?.id === node.id)
    if (cellIndex >= 0) {
      const template = getCollageTemplate(parent.templateId)
      const cell = template.cells[cellIndex]
      if (!cell) throw new Error(`Collage "${parent.id}" is missing template cell ${cellIndex + 1}`)
      const canvasRatio = documentOutputRatio(document, sources)
      const rect = collageCellRect(template, cell, { width: canvasRatio.w, height: canvasRatio.h }, parent.gap)
      return { w: rect.width, h: rect.height }
    }
    const crop = node.transforms[TransformKind.Crop]
    if (crop) return { w: crop.rect.width, h: crop.rect.height }
  }
  return nodeId === document.root.id
    ? documentOutputRatio(document, sources)
    : documentOutputRatio({ ...document, output: { ...document.output, split: null } }, sources)
}

export function nativeSize(
  document: Document,
  sources: ReadonlyMap<SourceId, Size>,
  nodeId: NodeId,
): Size {
  const node = findNode(document.root, nodeId)
  if (!node) throw new Error(`Cannot resolve a native size for unknown node "${nodeId}"`)

  switch (node.kind) {
    case NodeKind.Image: {
      const source = sources.get(node.sourceId)
      if (!source) throw new Error(`Image node "${node.id}" references missing source "${node.sourceId}"`)
      const crop = node.transforms[TransformKind.Crop]
      return crop
        ? { width: crop.rect.width, height: crop.rect.height }
        : orientedSize(source, node.transforms[TransformKind.Orientation])
    }
    case NodeKind.Frame: {
      const childSize = nativeSize(document, sources, node.child.id)
      if (node.params.styleId === FrameStyleId.Plain) {
        const pixelsPerMm = Math.min(childSize.width, childSize.height) / PLAIN_FRAME_INNER_SHORT_EDGE_MM
        const borderPixels = node.params.borderMm * pixelsPerMm
        return {
          width: Math.max(1, Math.round(childSize.width + borderPixels * 2)),
          height: Math.max(1, Math.round(childSize.height + borderPixels * 2)),
        }
      }
      const format = getFilmFormat(node.params.formatId)
      const scale = node.params.placement === PhotoPlacement.Free
        ? node.params.free?.scale
        : 1
      if (scale === undefined) throw new Error(`Frame node "${node.id}" is missing free placement parameters`)
      const pixelsPerMm = childSize.width / (format.windowMm.width * scale)
      const size = {
        width: Math.max(1, Math.round(format.filmMm.width * pixelsPerMm)),
        height: Math.max(1, Math.round(format.filmMm.height * pixelsPerMm)),
      }
      return size
    }
    case NodeKind.Collage: {
      const template = getCollageTemplate(node.templateId)
      const ratio = documentOutputRatio({ ...document, output: { ...document.output, split: null } }, sources)
      const ratioSize = { width: ratio.w, height: ratio.h }
      let supportedScale = Number.POSITIVE_INFINITY
      for (const [index, child] of node.cells.entries()) {
        if (!child) continue
        const cell = template.cells[index]
        if (!cell) throw new Error(`Collage "${node.id}" is missing template cell ${index + 1}`)
        const rect = collageCellRect(template, cell, ratioSize, node.gap)
        const childSize = nativeSize(document, sources, child.id)
        const childRect = nodeRenderRect(document, sources, child, { width: rect.width, height: rect.height })
        supportedScale = Math.min(
          supportedScale,
          childSize.width / childRect.width,
          childSize.height / childRect.height,
        )
      }
      if (!Number.isFinite(supportedScale)) {
        const preset = getOutputPreset(document.output.presetId)
        if (preset.targetSize) return { ...preset.targetSize }
        const originalRatio = documentOutputRatio(document, sources)
        return {
          width: 1080,
          height: Math.max(1, Math.round(1080 * originalRatio.h / originalRatio.w)),
        }
      }
      return {
        width: Math.max(1, Math.floor(ratioSize.width * supportedScale)),
        height: Math.max(1, Math.floor(ratioSize.height * supportedScale)),
      }
    }
    default:
      throw unknownNodeKind(node)
  }
}
